import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { queryClient } from "App";
import { FirmCardDropDown } from "app/components/common/FirmCardDropDown";
import PaymentModeComponent from "app/components/common/PaymentModeComponent";
import StaffSearchInput from "app/components/common/StaffSearchInput";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import useFolderWiseFileUploader from "app/hooks/upload/useFolderWiseFileUploader";
import { selectActiveSession } from "app/modules/schools/selectors";
import {
  addCompletedExpenseDetail,
  getAllExpenseHead,
  getAllExpenseSubHead,
} from "app/services/ledgerAccount";
import { getAllFirm } from "app/services/schoolService/firm.service";
import { getTransport } from "app/services/student-management";
import { MenuProps, ValidateFileSize } from "app/utils/helper";
import {
  CustomSelectBox,
  CustomTextArea,
  CustomTextField,
} from "assets/styles/globalStyledComponent";

// ============ Styled Components ============
const Label = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  lineHeight: "1.2rem",
  marginBottom: "10px",
  fontWeight: 500,
  color: "#0C2F49",
}));

const RequiredStar = () => <span style={{ color: "#DC2626" }}> *</span>;

const completedExpenseSchema = z
  .object({
    heads: z.array(z.any()).default([]),
    subHeads: z.array(z.any()).default([]),
    transportList: z.array(z.string()).default([]),
    head: z.string().min(1, "Head Selection is required"),
    subHead: z.string().min(1, "Sub Head Selection is required"),
    vehicle: z.string().optional(),
    type: z.string().min(1, "Type is required"),
    date: z.any(),
    dept: z.string().optional(),
    name: z.string().optional(),
    fName: z.string().optional(),
    amt: z.preprocess(
      (val) =>
        val === "" || val === null || typeof val === "undefined"
          ? undefined
          : Number(val),
      z
        .number({ required_error: "Amount is required" })
        .positive("Amount is required"),
    ),
    purpose: z.string().trim().min(1, "Purpose is required"),
    isVehicle: z.boolean().default(false),
    description: z.string().optional(),
    paymentDetails: z.any().nullable().optional(),
  })
  .superRefine((values, ctx) => {
    if (values.isVehicle && !values.vehicle) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["vehicle"],
        message: "Vehicle Selection is required",
      });
    }

    if (values.type === "Staff" && !values.name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["name"],
        message: "Name is required",
      });
    }

    if (values.type === "Other" && !values.fName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["fName"],
        message: "Firm Name is required",
      });
    }
  });

// ============ Helper Components ============
const StaffCard = ({ item }) => {
  return (
    <Box
      sx={{
        border: "1px solid #E0E7FF",
        borderRadius: "12px",
        padding: "12px 16px",
        backgroundColor: "#F8FAFF",
        boxShadow: "0px 2px 8px rgba(61, 57, 137, 0.08)",
        transition: "all 0.2s ease-in-out",
        minWidth: "280px",
        maxWidth: "400px",
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            position: "relative",
            width: "50px",
            height: "50px",
            borderRadius: "12px",
            overflow: "hidden",
            border: "2px solid #fff",
            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <img
            src={
              item?.profilePic ? item?.profilePic : "/image-placeholder.jpeg"
            }
            alt="staff"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
        <Stack flex={1} spacing={0.5}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "15px",
              color: "#1E293B",
              lineHeight: "1.3",
            }}
          >
            {item?.name}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Box
              sx={{
                backgroundColor: "#EEF2FF",
                borderRadius: "6px",
                padding: "2px 8px",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#4F46E5",
                }}
              >
                {item?.departmentName || "--"}
              </Typography>
            </Box>
            <Box
              sx={{
                backgroundColor: "#FEF3C7",
                borderRadius: "6px",
                padding: "2px 8px",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#92400E",
                }}
              >
                {item?.designation || "--"}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

// ============ Main Component ============
const CompletedExpenseForm = ({
  dialogs,
  onSuccess,
  onOpenSubHeadDialog,
  onSubmittingChange = () => {},
}) => {
  const selectedSession = useSelector(selectActiveSession);
  const { handleUploadImage } = useFolderWiseFileUploader();
  const paymentDetailsRef = useRef(null);
  const submitLockRef = useRef(false);

  // ============ State Management ============
  const [state, setState] = useState({
    image: null,
    firms: [],
    viaStaffDetail: {},
    recipientPersonDetail: {},
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ============ Form ============
  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(completedExpenseSchema),
    defaultValues: {
      subHeads: [],
      heads: [],
      head: "",
      subHead: "",
      vehicle: "",
      type: "Staff",
      date: new Date(),
      dept: "",
      name: "",
      fName: "",
      amt: "",
      purpose: "",
      isVehicle: false,
      transportList: [],
      description: "",
      paymentDetails: null,
    },
  });

  const values = watch();

  const onSubmit = (formValues) => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setIsSubmitting(true);
    onSubmittingChange(true);

    // Use payment details from ref if available, otherwise from formik values
    const finalValues = {
      ...formValues,
      paymentDetails: paymentDetailsRef.current || formValues.paymentDetails,
    };
    handleUploadServer(finalValues);
  };

  // ============ Handlers ============
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const { valid, message } = ValidateFileSize(file, {
      validateImage: true,
      validatePDF: true,
      imageLimit: 0.5 * 1048576, // 0.5 MB
    });

    if (!valid) {
      toast.error(message);
      return;
    }

    setState((prev) => ({
      ...prev,
      image: {
        file: file,
        link: URL.createObjectURL(file),
      },
    }));
  };

  const handleUploadServer = async (values) => {
    try {
      let attachmentUrl = state.image?.file
        ? await handleUploadImage(
            true,
            state.image.file,
            "withSession",
            "LEDGER",
            "EXPENSES",
          )
        : { fileURL: null };

      const payload = {
        session: selectedSession,
        expenseSubHeadDocId: values.subHead,
        ...(attachmentUrl && {
          attachmentUrl: attachmentUrl?.fileURL,
        }),
        vehicleNumber: values.vehicle,
        purpose: values.purpose,
        recipientPersonRole: values.type,
        recipientPersonDocId:
          values.type === "Staff" ? values.name : values.fName,
        expenseDate: values.date,
        paymentDetails: values.paymentDetails,
        ...(state.viaStaffDetail?.staffDocId && {
          viaPersonDocId: state.viaStaffDetail?.staffDocId,
        }),
      };
      addCompletedExpenseDetailMutate(payload);
    } catch (error) {
      toast.error(error);
      submitLockRef.current = false;
      setIsSubmitting(false);
      onSubmittingChange(false);
    }
  };

  const handleViaStaff = (payload) => {
    setState((prev) => ({ ...prev, viaStaffDetail: payload }));
  };

  const handleRecipientPerson = (payload) => {
    setState((prev) => ({ ...prev, recipientPersonDetail: payload }));
    if (payload?.staffDocId) {
      setValue("name", payload.staffDocId, { shouldValidate: true });
      setValue("type", "Staff", { shouldValidate: true });
      setValue("dept", payload.departmentName || payload.department || "N/A", {
        shouldValidate: false,
      });
    }
  };

  const removeViaStaff = () => {
    setState((prev) => ({ ...prev, viaStaffDetail: {} }));
  };

  const removeRecipientPerson = () => {
    setState((prev) => ({ ...prev, recipientPersonDetail: {} }));
    setValue("name", "", { shouldValidate: true });
  };

  // ============ API Mutations ============
  const { mutate: addCompletedExpenseDetailMutate } = useMutation(
    addCompletedExpenseDetail,
    {
      onSuccess: (res) => {
        toast.success(res?.data?.message);
        queryClient.invalidateQueries({ queryKey: "getAllExpense" });
        submitLockRef.current = false;
        setIsSubmitting(false);
        onSubmittingChange(false);
        onSuccess();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message);
        submitLockRef.current = false;
        setIsSubmitting(false);
        onSubmittingChange(false);
      },
    },
  );

  // ============ API Queries ============
  useQuery({
    queryKey: ["getTransport"],
    queryFn: () => getTransport({ status: "ACTIVE" }),
    onSuccess: (res) => {
      const transportList =
        res?.data?.list?.map((item) => item.vehicleNumber) || [];
      setValue("transportList", transportList, { shouldValidate: false });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
    enabled: values.isVehicle,
  });

  useQuery({
    queryKey: ["getAllExpenseHead", selectedSession],
    queryFn: getAllExpenseHead,
    onSuccess: (res) => {
      const heads =
        res?.data?.list?.map((item) => ({
          id: item._id,
          name: item.expenseHeadName,
        })) || [];

      setValue("heads", heads, { shouldValidate: false });
      if (dialogs?.subHead) {
        setValue("subHead", dialogs.subHead, { shouldValidate: true });
      }
      if (dialogs?.headId) {
        setValue("head", dialogs.headId, { shouldValidate: true });
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
    enabled: dialogs?.add ? true : false,
  });

  useQuery({
    queryKey: ["getAllExpenseSubHeadForCom", values.head],
    queryFn: () => getAllExpenseSubHead({ expenseHeadDocId: values.head }),
    onSuccess: (res) => {
      const subHeads =
        res?.data?.list?.map((item) => ({
          id: item._id,
          name: item.expenseSubHeadName,
          description: item.description,
        })) || [];

      setValue("subHeads", subHeads, { shouldValidate: false });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
    enabled: values.head ? true : false,
  });

  useQuery({
    queryKey: "getAllFirm",
    queryFn: () => getAllFirm({}),
    enabled: values.type === "Other",
    onSuccess: (res) => {
      setState((prev) => ({ ...prev, firms: res?.data?.list || [] }));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
  });

  // ============ Render ============
  return (
    <>
      <Box>
        <Grid container rowSpacing={1.5} columnSpacing={1} mt={1}>
          {/* Expense Head */}
          <Grid item xs={6} md={4}>
            <Label>
              Expense Head
              <RequiredStar />
            </Label>
            <CustomSelectBox
              name="head"
              fullWidth
              size="small"
              value={values.head}
              displayEmpty
              MenuProps={MenuProps}
              onChange={(e) => {
                setValue("head", e.target.value, { shouldValidate: true });
                setValue("subHead", "", { shouldValidate: true });
                setValue("description", "", { shouldValidate: false });
              }}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              {values.heads?.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </CustomSelectBox>
            {errors.head && (
              <Typography sx={{ color: "red" }}>
                {errors.head.message}
              </Typography>
            )}
          </Grid>

          {/* Sub Head */}
          <Grid item xs={6} md={4}>
            <Label>
              Sub Head
              <RequiredStar />
            </Label>
            <CustomSelectBox
              name="subHead"
              disabled={!values.head}
              fullWidth
              size="small"
              displayEmpty
              value={values.subHead}
              MenuProps={MenuProps}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              {values.subHeads?.map((item) => (
                <MenuItem
                  key={item.id}
                  value={item?.id}
                  onClick={() => {
                    setValue("subHead", item.id, { shouldValidate: true });
                    setValue("description", item?.description || "", {
                      shouldValidate: false,
                    });
                  }}
                >
                  {item.name}
                </MenuItem>
              ))}
              <MenuItem
                sx={{
                  color: "blue",
                  textDecoration: "underline",
                  fontStyle: "italic",
                }}
                value="Add New"
                onClick={() => {
                  onOpenSubHeadDialog(values.head);
                  setValue("subHead", "", { shouldValidate: true });
                }}
              >
                Add New
              </MenuItem>
            </CustomSelectBox>
            {errors.subHead && (
              <Typography sx={{ color: "red" }}>
                {errors.subHead.message}
              </Typography>
            )}
          </Grid>

          {/* Vehicle Expense */}
          <Grid item xs={6} md={4}>
            <Stack direction="row" alignItems="center">
              <Label>Vehicle Expense ?</Label>
              <Stack direction="row" alignItems="center" sx={{ mt: "-10px" }}>
                <Checkbox
                  checked={values.isVehicle}
                  onChange={(e) =>
                    setValue("isVehicle", e.target.checked, {
                      shouldValidate: true,
                    })
                  }
                />
                <Typography>Yes</Typography>
              </Stack>
            </Stack>
            {values.isVehicle && (
              <>
                <Label>
                  Vehicle Number
                  <RequiredStar />
                </Label>
                <CustomSelectBox
                  fullWidth
                  size="small"
                  displayEmpty
                  name="vehicle"
                  value={values.vehicle}
                  MenuProps={MenuProps}
                  onChange={(e) => {
                    setValue("vehicle", e.target.value, {
                      shouldValidate: true,
                    });
                  }}
                >
                  <MenuItem value="" disabled>
                    Select
                  </MenuItem>
                  {values.transportList?.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </CustomSelectBox>
                {errors.vehicle && (
                  <Typography sx={{ color: "red" }}>
                    {errors.vehicle.message}
                  </Typography>
                )}
              </>
            )}
          </Grid>

          {/* Sub Head Description */}
          <Grid item xs={12}>
            <Label>Sub Head Description</Label>
            <Typography>{values.description || "--"}</Typography>
          </Grid>

          {/* Via Staff Selection */}
          <Grid item xs={12}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              gap={2}
              alignItems={{ xs: "flex-start", md: "flex-start" }}
              justifyContent="space-between"
            >
              <Box sx={{ minWidth: "200px" }}>
                <TextFieldLabel title={"Via"} />
                <StaffSearchInput returnResponse={handleViaStaff} />
              </Box>

              {Object.keys(state.viaStaffDetail).length > 0 && (
                <Stack
                  direction="row"
                  gap={1.5}
                  alignItems="center"
                  sx={{ ml: { xs: 0, md: "auto" } }}
                >
                  <StaffCard item={state.viaStaffDetail} />
                  <IconButton
                    sx={{
                      background: "#FEE2E2",
                      width: "36px",
                      height: "36px",
                    }}
                    onClick={removeViaStaff}
                  >
                    <Close sx={{ color: "#DC2626", fontSize: "20px" }} />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          </Grid>

          {/* Recipient Person Type */}
          <Grid item xs={6} md={4}>
            <Label>
              Recipient Person Type
              <RequiredStar />
            </Label>
            <CustomSelectBox
              name="type"
              fullWidth
              size="small"
              value={values.type}
              onChange={(e) => {
                const nextType = e.target.value;
                setValue("type", nextType, { shouldValidate: true });
                if (nextType !== "Staff") {
                  setValue("name", "", { shouldValidate: true });
                }
                if (nextType !== "Other") {
                  setValue("fName", "", { shouldValidate: true });
                }
                if (nextType !== "Staff") {
                  setState((prev) => ({
                    ...prev,
                    recipientPersonDetail: {},
                  }));
                }
              }}
              displayEmpty
              MenuProps={MenuProps}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              <MenuItem value="Staff">Staff</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </CustomSelectBox>
            {errors.type && (
              <Typography sx={{ color: "red" }}>
                {errors.type.message}
              </Typography>
            )}
          </Grid>

          {/* Firm Name - for Other type */}
          {values.type === "Other" && (
            <Grid item xs={6} md={4}>
              <Label>
                Firm Name
                <RequiredStar />
              </Label>
              <CustomSelectBox
                fullWidth
                size="small"
                MenuProps={MenuProps}
                name="fName"
                displayEmpty
                value={values.fName}
                onChange={(e) =>
                  setValue("fName", e.target.value, { shouldValidate: true })
                }
                renderValue={
                  values.fName
                    ? () => {
                        const item = state.firms?.find(
                          (item) => item._id === values.fName,
                        );
                        return item?.firmName;
                      }
                    : null
                }
              >
                <MenuItem value="" disabled>
                  Select
                </MenuItem>
                {state?.firms?.map((item) => (
                  <MenuItem key={item._id} value={item._id}>
                    <FirmCardDropDown item={item} />
                  </MenuItem>
                ))}
              </CustomSelectBox>
              {errors.fName && (
                <Typography sx={{ color: "red" }}>
                  {errors.fName.message}
                </Typography>
              )}
            </Grid>
          )}

          {/* Recipient Person - for Staff type */}
          {values.type === "Staff" && (
            <Grid item xs={12} md={8}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                gap={2}
                alignItems={{ xs: "flex-start", md: "flex-start" }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: "200px" }}>
                  <TextFieldLabel title={"Recipient Person"} required />
                  <StaffSearchInput returnResponse={handleRecipientPerson} />
                </Box>
              </Stack>
            </Grid>
          )}
          {errors.name && values.type === "Staff" && (
            <Grid item xs={12}>
              <Typography sx={{ color: "red" }}>
                {errors.name.message}
              </Typography>
            </Grid>
          )}
          {Object.keys(state.recipientPersonDetail).length > 0 && (
            <Grid item xs={12} md={9}>
              <Stack
                direction="row"
                gap={1.5}
                alignItems="center"
                sx={{ ml: { xs: 0, md: "auto" } }}
              >
                <StaffCard item={state.recipientPersonDetail} />
                <IconButton
                  sx={{
                    background: "#FEE2E2",
                    width: "36px",
                    height: "36px",
                  }}
                  onClick={removeRecipientPerson}
                >
                  <Close sx={{ color: "#DC2626", fontSize: "20px" }} />
                </IconButton>
              </Stack>
            </Grid>
          )}

          {/* Purpose */}
          <Grid item xs={12}>
            <Label>
              Purpose
              <RequiredStar />
            </Label>
            <CustomTextArea
              placeholder="Enter Purpose"
              name="purpose"
              value={values.purpose}
              onChange={(e) =>
                setValue("purpose", e.target.value, { shouldValidate: true })
              }
            />

            {/* <CustomTextField
              fullWidth
              size="small"
              placeholder="Enter Purpose"
              name="purpose"
              value={formik.values.purpose}
              onChange={formik.handleChange}
            /> */}
            {errors.purpose && (
              <Typography sx={{ color: "red" }}>
                {errors.purpose.message}
              </Typography>
            )}
          </Grid>

          {/* Expense Date */}
          <Grid item xs={6} md={4}>
            <Label>Expense Date</Label>
            <DatePicker
              inputFormat="DD-MM-YYYY"
              name="collectionDate"
              value={values.date}
              onChange={(e) => {
                setValue("date", e, { shouldValidate: false });
              }}
              maxDate={new Date()}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  placeholder="Select"
                  fullWidth
                  size="small"
                />
              )}
            />
          </Grid>

          {/* Expended Amount */}
          <Grid item xs={6} md={4}>
            <Label>
              Expended Amount
              <RequiredStar />
            </Label>
            <CustomTextField
              fullWidth
              size="small"
              placeholder="Enter Amount"
              type="number"
              value={values.amt}
              name="amt"
              onChange={(e) =>
                setValue("amt", e.target.value, { shouldValidate: true })
              }
            />
            {errors.amt && (
              <Typography sx={{ color: "red" }}>
                {errors.amt.message}
              </Typography>
            )}
          </Grid>

          {/* Attachment Upload */}
          <Grid item xs={6} md={4}>
            <Label>Attachment(if any)</Label>
            <Box>
              <img
                src={
                  state?.image?.link
                    ? state?.image?.link
                    : "/image-placeholder.jpeg"
                }
                style={{
                  height: "100px",
                  width: "100px",
                  objectFit: "cover",
                  marginTop: "10px",
                  marginBottom: "10px",
                }}
                alt="ekalsutra"
              />
            </Box>
            <Box>
              <Button variant="contained" color="secondary" component="label">
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  hidden
                  onChange={handleFileUpload}
                />
                Upload
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Stack mt={2}>
        <PaymentModeComponent
          amount={values.amt}
          formId="comp-exp-pay"
          onClickSubmit={(data) => {
            if (isSubmitting || submitLockRef.current) return;
            // Store payment details in ref for immediate access
            paymentDetailsRef.current = data;
            // Trigger validation and submit
            handleSubmit(onSubmit)();
          }}
        />
      </Stack>
    </>
  );
};

export default CompletedExpenseForm;
